/**
 * 形象系统：一身灰、一身味、疲劳、以及「这人看着像不像这儿的」。
 *
 * 数值都来自两处：
 *   tags 表        — dirty / stink 这类跟着你走的标签
 *   player 表      — fatigue（疲劳）、outfitCleanliness（衣裳洁净）、aura（属于这里的程度）
 * 别的系统要判断「NPC 怎么看你」「能不能进某扇门」，都来问这里，不自己算。
 */
import { Repo } from '../data/schema.js';
import { Tags } from './tags.js';

/** 脏到什么程度会有人说闲话 */
export const DIRTY_NOTICE = 25;
/** 味影响到别人的距离 */
export const STINK_NOTICE = 20;

const CLEAN_WORDS = [
  [0.8, '衣裳还算齐整'], [0.6, '袖口有点脏'], [0.4, '看得出是干过活的'],
  [0.2, '一身尘土'], [0, '看不出原来的颜色'],
];
const DIRTY_WORDS = [
  [60, '你身上的味道，隔着两步就闻得见'], [40, '灰扑扑的，人家多看你一眼'],
  [20, '手是黑的'], [0, ''],
];

function pick(words, value) {
  for (const [th, text] of words) if (value >= th) return text;
  return '';
}

export const Avatar = {
  /** 综合形象，给 UI 与各系统用 */
  get(db) {
    const p = Repo.getPlayer(db);
    const dirty = Tags.get(db, 'dirty');
    const stink = Tags.get(db, 'stink');
    const cleanliness = Math.max(0, Math.min(1, Number(p.outfitCleanliness == null ? 1 : p.outfitCleanliness)));
    const fatigue = Math.max(0, Math.min(120, Number(p.fatigue || 0)));
    return {
      dirty: dirty.total,
      dirtyStage: dirty.stage,
      stink: stink.total,
      stinkStage: stink.stage,
      cleanliness,
      fatigue,
      aura: Math.max(0, Math.min(1, Number(p.aura == null ? 1 : p.aura))),
      noted: dirty.total >= DIRTY_NOTICE,
      smelly: stink.total >= STINK_NOTICE,
    };
  },

  /** 一句话概括现在这副样子 */
  describe(db) {
    const a = this.get(db);
    if (a.dirty >= DIRTY_NOTICE) return pick(DIRTY_WORDS, a.dirty) || '蓬头垢面';
    return pick(CLEAN_WORDS, a.cleanliness) || '干净利落';
  },

  /**
   * 别人对你的估价偏多少：脏、臭就把价压下来，体面则抬一点。
   * 返回倍率，1 为不动。
   */
  priceFactor(db) {
    const a = this.get(db);
    let f = 1;
    if (a.dirty >= DIRTY_NOTICE) f -= 0.05 * Math.min(3, Math.floor((a.dirty - DIRTY_NOTICE) / 25) + 1);
    if (a.stink >= STINK_NOTICE) f -= 0.05 * Math.min(3, Math.floor((a.stink - STINK_NOTICE) / 20) + 1);
    if (a.cleanliness > 0.7 && a.dirty < 20) f += 0.05;
    return Math.max(0.6, Math.min(1.1, f));
  },

  /** 疲劳：0~120，越高越使不上劲 */
  addFatigue(db, amount) {
    const p = Repo.getPlayer(db);
    const next = Math.max(0, Math.min(120, Number(p.fatigue || 0) + amount));
    Repo.savePlayer(db, { fatigue: next });
    return next;
  },

  rest(db, amount = 999) {
    const p = Repo.getPlayer(db);
    const next = Math.max(0, Number(p.fatigue || 0) - amount);
    Repo.savePlayer(db, { fatigue: next });
    return next;
  },

  /** 干活/作践衣裳时调用：衣裳渐脏 */
  soil(amount = 0.05) {
    return amount;
  },

  /**
   * 擦洗：就着清水搓一搓，有皂角效果更好。
   */
  wash(db, { soap = false } = {}) {
    const p = Repo.getPlayer(db);
    const a = this.get(db);
    if ((p.energy || 0) < 3) return { ok: false, reason: '手上没劲，拧不动布。' };
    const cutD = soap ? 18 : 9;
    const cutS = soap ? 20 : 10;
    Tags.reduce(db, 'dirty', cutD);
    Tags.reduce(db, 'stink', cutS);
    const cleanliness = Math.min(1, a.cleanliness + (soap ? 0.25 : 0.12));
    Repo.savePlayer(db, {
      outfitCleanliness: cleanliness,
      energy: Math.max(0, (p.energy || 0) - 3),
    });
    if (soap) Repo.takeItem(db, 'soapNut', 1);
    return { ok: true, text: soap ? '打了皂角，搓下一层泥。' : '就着清水擦了擦，好些了。' };
  },

  /** 泡澡：去味最彻底，要花几文钱 */
  bathe(db, price = 5) {
    const p = Repo.getPlayer(db);
    if ((p.cash || 0) < price) return { ok: false, reason: `泡一次要${price}钱，钱不够。` };
    if ((p.energy || 0) < 2) return { ok: false, reason: '困得眼睛都睁不开了，改天再泡。' };
    Repo.savePlayer(db, {
      cash: (p.cash || 0) - price,
      outfitCleanliness: 1,
      energy: Math.min(100, (p.energy || 0) + 8),
      mood: Math.min(100, (p.mood || 0) + 6),
    });
    Tags.reduce(db, 'dirty', 999);
    Tags.reduce(db, 'stink', 999);
    return { ok: true, text: '热水一泡，一身灰跟着汗水走了。' };
  },

  /** 换身干净衣裳：有衣服就能换，是最快的遮掩 */
  changeClothes(db) {
    if (!Repo.takeItem(db, 'usedClothing', 1)) return { ok: false, reason: '身上没有能换的衣裳。' };
    const dirty = Tags.get(db, 'dirty').total;
    Tags.reduce(db, 'dirty', 15);
    Tags.reduce(db, 'stink', 8);
    Repo.savePlayer(db, { outfitCleanliness: 0.8 });
    return { ok: true, text: dirty > 40 ? '换了身衣裳，至少看着像个人了。' : '换了衣裳，清爽些。' };
  },

  /** 「属于这里」的程度：体面、有照、有地都往上加 */
  updateAura(db) {
    const p = Repo.getPlayer(db);
    const a = this.get(db);
    let aura = 1;
    if (a.dirty >= DIRTY_NOTICE) aura -= 0.1;
    if (a.stink >= STINK_NOTICE) aura -= 0.1;
    if (Tags.has(db, 'residencePermit')) aura += 0.1;
    if (Tags.has(db, 'workshopLicense')) aura += 0.1;
    if (Tags.has(db, 'landDeed')) aura += 0.1;
    if (Tags.get(db, 'face').total >= 30) aura += 0.1;
    aura = Math.max(0, Math.min(1.5, aura));
    Repo.savePlayer(db, { aura });
    return aura;
  },

  /** 天亮时：衣裳继续返潮，疲劳降一点 */
  daily(db) {
    const p = Repo.getPlayer(db);
    const clean = Math.max(0, Number(p.outfitCleanliness == null ? 1 : p.outfitCleanliness) - 0.05);
    const fatigue = Math.max(0, Number(p.fatigue || 0) - 20);
    Repo.savePlayer(db, { outfitCleanliness: clean, fatigue });
    this.updateAura(db);
    return { clean, fatigue };
  },
};
